import React, { useEffect, useState } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import Cookies from 'js-cookie';
import { Page, LayoutSingleColumn, IconSpinner } from '../../components';
import css from './AppleSignInPage.module.css';

/**
 * Apple Sign-In Page
 * Handles the Apple Sign-In flow using the Apple Sign-In JavaScript SDK
 */
const AppleSignInPage = ({ location, history }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if we're returning from Apple Sign-In redirect callback
    // Apple sends data via POST to the redirectURI, but also via URL fragment/params
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));

    // Try to get auth data from URL params (query string or hash fragment)
    const code = urlParams.get('code') || hashParams.get('code');
    const idToken = urlParams.get('id_token') || hashParams.get('id_token');
    const userParam = urlParams.get('user') || hashParams.get('user');
    const errorParam = urlParams.get('error') || hashParams.get('error');

    // Handle error returned from Apple
    if (errorParam) {
      console.error('Apple Sign-In error from redirect:', errorParam);
      setError('Apple Sign-In was cancelled or failed. Please try again.');
      setIsLoading(false);
      return;
    }

    // If we have auth data from redirect, process it
    if (code || idToken) {
      setIsLoading(true);
      // Construct a response object matching the expected format
      let parsedUser = null;
      if (userParam) {
        try {
          parsedUser = JSON.parse(decodeURIComponent(userParam));
        } catch (e) {
          console.warn('Failed to parse user data from redirect:', e);
        }
      }
      const callbackResponse = {
        authorization: { code, id_token: idToken },
        user: parsedUser,
      };
      handleAppleSignInResponse(callbackResponse);
      return;
    }

    // Initialize Apple Sign-In SDK for redirect flow
    if (window.AppleID) {
      try {
        window.AppleID.auth.init({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
          teamId: process.env.REACT_APP_APPLE_TEAM_ID,
          redirectURI: `${window.location.origin}/apple-signin`,
          scope: 'email name',
          usePopup: false,
        });
      } catch (err) {
        console.error('Error initializing Apple Sign-In SDK:', err);
        setError('Failed to initialize Apple Sign-In. Please try again.');
        setIsLoading(false);
        return;
      }

      // Trigger redirect to Apple Sign-In
      triggerAppleSignIn();
    } else {
      console.warn('Apple Sign-In SDK failed to load. Please refresh the page.');
      setError('Apple Sign-In SDK failed to load. Please refresh the page.');
      setIsLoading(false);
    }
  }, []);

  const triggerAppleSignIn = () => {
    try {
      setIsLoading(true);
      if (window.AppleID && window.AppleID.auth) {
        // In redirect mode, signIn() redirects the user to Apple's login page
        // After authentication, Apple redirects back to our redirectURI with auth data
        // The signIn() call does not return a response in redirect mode
        window.AppleID.auth.signIn();
        // Note: The user will be redirected away from this page
        // When they return, the useEffect will handle the callback data
      } else {
        console.error('Apple Sign-In SDK is not available');
        setError('Apple Sign-In SDK is not available. Please refresh the page.');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Apple Sign-In error:', err);
      setError('Apple Sign-In failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleAppleSignInResponse = async (response) => {
    try {
      setIsLoading(true);

      // Extract user info from response
      // Handle both popup response format and redirect callback format
      const idToken = response.authorization?.id_token || response.id_token || response.identityToken;
      const userInfo = response.user || {};
      const email = userInfo.email || response.email;
      const firstName = userInfo.name?.firstName || '';
      const lastName = userInfo.name?.lastName || '';

      if (!idToken) {
        console.error('Missing idToken. Response:', response);
        throw new Error('No ID token received from Apple');
      }

      // Get auth parameters from cookie
      // Note: Using Cookies.get() + JSON.parse() instead of deprecated getJSON()
      const authParamsCookie = Cookies.get('st-apple-auth-params');
      const authParams = authParamsCookie
        ? JSON.parse(authParamsCookie.replace('j:', ''))
        : {};
      const { userType } = authParams;

      if (!userType) {
        console.warn('userType not found in cookie - signup may fail for new users');
      }

      // Call Firebase Cloud Function to handle signup/login
      const socialSignup = httpsCallable(functions, 'socialSignup');
      const result = await socialSignup({
        provider: 'apple',
        idToken,
        userType,
        profileData: {
          firstName,
          lastName,
          email,
        },
      });

      // Handle successful authentication
      const { access_token } = result.data;

      // Store tokens and redirect
      localStorage.setItem('st_access_token', access_token);

      // Redirect to landing page or specified return URL
      const returnUrl = authParams.defaultReturn || '/';
      window.location.href = returnUrl;

    } catch (err) {
      console.error('Error handling Apple Sign-In response:', err);
      setError(err.message || 'Authentication failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Page title="Apple Sign-In" scrollingDisabled={isLoading}>
      <LayoutSingleColumn topbar={null} footer={null}>
        <div className={css.root}>
          {isLoading ? (
            <div className={css.loadingContainer}>
              <IconSpinner />
              <p>Signing in with Apple...</p>
            </div>
          ) : error ? (
            <div className={css.errorContainer}>
              <p className={css.error}>{error}</p>
              <button onClick={() => window.history.back()}>Go Back</button>
            </div>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default AppleSignInPage;

